import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import { PTTMode, RecordingStatus } from '@/types';

interface PTTContextValue {
  mode: PTTMode;
  status: RecordingStatus;
  isRecording: boolean;
  isProcessing: boolean;
  setMode: (mode: PTTMode) => void;
  setIsProcessing: (processing: boolean) => void;
  startRecording: () => void;
  stopRecording: () => Promise<Blob | null>;
}

const PTTContext = createContext<PTTContextValue | undefined>(undefined);

export function PTTProvider({ children }: { children: React.ReactNode }) {
  const [mode, setMode] = useState<PTTMode>(PTTMode.MANUAL);
  const [status, setStatus] = useState<RecordingStatus>('idle');
  const [isProcessing, setIsProcessing] = useState(false);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const recordingStartTimeRef = useRef<number>(0);

  const isRecording = status === 'listening';

  // Start audio recording
  const startRecording = useCallback(async () => {
    if (isRecording) {
      console.warn('Already recording');
      return;
    }

    try {
      // Request microphone access
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          sampleRate: 16000,
          echoCancellation: true,
          noiseSuppression: true,
        },
      });

      streamRef.current = stream;
      audioChunksRef.current = [];

      // Create MediaRecorder
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: 'audio/webm',
      });

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorderRef.current = mediaRecorder;
      recordingStartTimeRef.current = Date.now();
      mediaRecorder.start();
      setStatus('listening');

      console.log('🎤 Recording started');
    } catch (error) {
      console.error('Failed to start recording:', error);
      setStatus('idle');
      throw error;
    }
  }, [isRecording]);

  // Stop audio recording and return blob
  const stopRecording = useCallback((): Promise<Blob | null> => {
    return new Promise((resolve) => {
      if (!isRecording || !mediaRecorderRef.current) {
        console.warn('Not recording');
        resolve(null);
        return;
      }

      const mediaRecorder = mediaRecorderRef.current;

      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        const duration = Date.now() - recordingStartTimeRef.current;

        // Stop all tracks
        if (streamRef.current) {
          streamRef.current.getTracks().forEach(track => track.stop());
          streamRef.current = null;
        }

        // Cleanup
        mediaRecorderRef.current = null;
        audioChunksRef.current = [];
        recordingStartTimeRef.current = 0;
        setStatus('idle');

        console.log('🎤 Recording stopped, blob size:', audioBlob.size, 'duration:', duration, 'ms');

        // Skip if duration is less than 2 seconds
        if (duration < 2000) {
          console.log('⏭️ Recording too short, skipping');
          resolve(null);
          return;
        }

        resolve(audioBlob);
      };

      mediaRecorder.stop();
    });
  }, [isRecording]);

  const value: PTTContextValue = {
    mode,
    status,
    isRecording,
    isProcessing,
    setMode,
    setIsProcessing,
    startRecording,
    stopRecording,
  };

  return (
    <PTTContext.Provider value={value}>
      {children}
    </PTTContext.Provider>
  );
}

export function usePTT() {
  const context = useContext(PTTContext);
  if (context === undefined) {
    throw new Error('usePTT must be used within PTTProvider');
  }
  return context;
}
